/**
 * OnlyPaithani Testimonials & Customer Stories JavaScript
 * Accessible slider, star selector, and smooth scroll interactions.
 */

document.addEventListener('DOMContentLoaded', () => {
  initSmoothScroll();
  initTestimonialSliders();
  initStarRatingSelectors();
});

// Also re-initialize when Shopify Theme Editor reloads or updates sections
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initSmoothScroll();
    initTestimonialSliders();
    initStarRatingSelectors();
  });
}

/**
 * Smooth Scrolling for Anchor Links
 */
function initSmoothScroll() {
  const links = document.querySelectorAll('a[href^="#"]:not([href="#"])');
  links.forEach(link => {
    link.addEventListener('click', function (e) {
      const targetId = this.getAttribute('href');
      if (!targetId || targetId === '#') return;
      const targetEl = document.querySelector(targetId);
      if (targetEl) {
        e.preventDefault();
        targetEl.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start'
        });
      }
    });
  });
}

/**
 * Accessible Testimonial Slider Class
 */
class OpTestimonialSlider {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.op-slider-track');
    this.slides = container.querySelectorAll('.op-slider-slide');
    this.prevBtn = container.querySelector('.op-slider-prev');
    this.nextBtn = container.querySelector('.op-slider-next');
    this.dotsContainer = container.querySelector('.op-slider-dots');

    if (!this.track || this.slides.length === 0) return;

    this.currentIndex = 0;
    this.slidesToShow = this.getSlidesToShow();
    this.maxIndex = Math.max(0, this.slides.length - this.slidesToShow);

    this.startX = 0;
    this.currentX = 0;
    this.isDragging = false;

    this.init();
  }

  getSlidesToShow() {
    const width = window.innerWidth;
    const layout = this.container.getAttribute('data-slides-per-view');
    if (layout === '3') {
      return width >= 990 ? 3 : width >= 768 ? 2 : 1;
    } else if (layout === '2') {
      return width >= 768 ? 2 : 1;
    }
    return 1;
  }

  init() {
    this.renderDots();
    this.update();

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }

    // Touch & Drag Events
    this.track.addEventListener('touchstart', (e) => this.touchStart(e), { passive: true });
    this.track.addEventListener('touchmove', (e) => this.touchMove(e), { passive: true });
    this.track.addEventListener('touchend', () => this.touchEnd());

    // Keyboard navigation
    this.container.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prev();
      } else if (e.key === 'ArrowRight') {
        this.next();
      }
    });

    // Resize handler
    window.addEventListener('resize', () => {
      const newSlidesToShow = this.getSlidesToShow();
      if (newSlidesToShow !== this.slidesToShow) {
        this.slidesToShow = newSlidesToShow;
        this.maxIndex = Math.max(0, this.slides.length - this.slidesToShow);
        if (this.currentIndex > this.maxIndex) {
          this.currentIndex = this.maxIndex;
        }
        this.renderDots();
        this.update();
      }
    });
  }

  renderDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    const totalDots = this.maxIndex + 1;
    if (totalDots <= 1) return;

    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `op-slider-dot ${i === this.currentIndex ? 'is-active' : ''}`;
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => this.goTo(i));
      this.dotsContainer.appendChild(dot);
    }
  }

  goTo(index) {
    this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
    this.update();
  }

  next() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
      this.update();
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.update();
    }
  }

  update() {
    const slideWidthPercent = 100 / this.slidesToShow;
    const moveOffset = -(this.currentIndex * slideWidthPercent);
    this.track.style.transform = `translateX(${moveOffset}%)`;

    // Update buttons state
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
      this.prevBtn.setAttribute('aria-disabled', this.currentIndex === 0);
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
      this.nextBtn.setAttribute('aria-disabled', this.currentIndex >= this.maxIndex);
    }

    // Update dots
    if (this.dotsContainer) {
      const dots = this.dotsContainer.querySelectorAll('.op-slider-dot');
      dots.forEach((dot, idx) => {
        if (idx === this.currentIndex) {
          dot.classList.add('is-active');
          dot.setAttribute('aria-selected', 'true');
        } else {
          dot.classList.remove('is-active');
          dot.setAttribute('aria-selected', 'false');
        }
      });
    }
  }

  touchStart(e) {
    this.startX = e.touches[0].clientX;
    this.isDragging = true;
  }

  touchMove(e) {
    if (!this.isDragging) return;
    this.currentX = e.touches[0].clientX;
  }

  touchEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    const diffX = this.startX - this.currentX;
    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        this.next();
      } else {
        this.prev();
      }
    }
  }
}

function initTestimonialSliders() {
  const sliders = document.querySelectorAll('.op-slider-wrapper');
  sliders.forEach(slider => {
    if (!slider.dataset.sliderInitialized) {
      new OpTestimonialSlider(slider);
      slider.dataset.sliderInitialized = 'true';
    }
  });
}

/**
 * Star Rating Radio Selector for Form
 */
function initStarRatingSelectors() {
  const starContainers = document.querySelectorAll('.op-rating-select-group');
  starContainers.forEach(container => {
    const hiddenInput = container.querySelector('input[type="hidden"]');
    const buttons = container.querySelectorAll('.op-star-radio-btn');

    buttons.forEach((btn, index) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const ratingVal = index + 1;
        if (hiddenInput) hiddenInput.value = ratingVal;

        buttons.forEach((b, i) => {
          if (i <= index) {
            b.classList.add('is-selected');
            b.setAttribute('aria-checked', 'true');
          } else {
            b.classList.remove('is-selected');
            b.setAttribute('aria-checked', 'false');
          }
        });
      });
    });
  });
}
